import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import PortalLayout from "@/components/ventrix/PortalLayout";
import TrackingTimeline from "@/components/ventrix/TrackingTimeline";
import { api, formatApiErrorDetail } from "@/lib/api";
import { StatusChip, ModeIcon, formatDate, formatDateShort, formatMoney } from "@/components/ventrix/atoms";
import { useAuth, isStaff } from "@/context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, MapPin, AlertOctagon, Zap, Printer, FileText, Plus, Trash2, Save, Route, DollarSign, Calculator, CheckCircle2, Clock } from "lucide-react";
import TrackingMap from "@/components/ventrix/TrackingMap";

const STATUS_CODES = ["BOOKED","PICKED_UP","IN_TRANSIT","AT_HUB","CUSTOMS","OUT_FOR_DELIVERY","DELIVERED"];
const EXCEPTION_CODES = [
  { v: "DELAY", l: "Delay" },
  { v: "DAMAGE", l: "Damage" },
  { v: "CUSTOMS_HOLD", l: "Customs hold" },
  { v: "DOC_MISSING", l: "Missing documents" },
  { v: "WEATHER", l: "Weather disruption" },
];

export default function ShipmentDetail({ section = "customer" }) {
  const { id } = useParams();
  const { user } = useAuth();
  const [ship, setShip] = useState(null);
  const [route, setRoute] = useState(null);
  const [routeLoading, setRouteLoading] = useState(true);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/v1/shipments/${id}`).then(({ data }) => setShip(data.shipment));
  const loadRoute = () => {
    setRouteLoading(true);
    api.get(`/v1/shipments/${id}/route`)
      .then(({ data }) => setRoute(data))
      .catch(() => setRoute(null))
      .finally(() => setRouteLoading(false));
  };
  useEffect(() => { load(); loadRoute(); }, [id]); // eslint-disable-line

  const staff = isStaff(user?.role);

  const addNote = async () => {
    if (!note.trim()) return;
    setBusy(true);
    try {
      await api.post(`/v1/shipments/${id}/notes`, { body: note });
      setNote(""); await load(); toast.success("Note added");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!ship) return <PortalLayout section={section}><div className="vtx-skeleton h-24" /></PortalLayout>;

  return (
    <PortalLayout section={section}>
      <Link to={section === "ops" ? "/ops/shipments" : "/app/shipments"} className="inline-flex items-center gap-1.5 text-xs text-[#475569] hover:text-[#0E2F76] mb-4 font-semibold" data-testid="back-link">
        <ArrowLeft size={14}/> Back to shipments
      </Link>

      <div className="vtx-card p-6 md:p-8 mb-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 text-sm">
              <ModeIcon mode={ship.is_multimodal ? "multimodal" : ship.mode} showLabel />
              <span className="text-[#A9C0E0]">·</span>
              <span className="uppercase tracking-widest text-[11px] font-bold text-[#475569]">{ship.service_level}</span>
              <span className="text-[#A9C0E0]">·</span>
              <span className="font-mono text-xs text-[#475569]">{ship.booking_ref}</span>
            </div>
            <div className="font-mono text-3xl text-[#0E2F76] font-bold mt-2 tracking-tight">{ship.tracking_number}</div>
            <div className="mt-2 text-[#0f172a]">
              <span className="font-semibold">{ship.origin}</span> <span className="text-[#A9C0E0]">→</span> <span className="font-semibold">{ship.destination}</span>
            </div>
          </div>
          <div className="text-right">
            <StatusChip status={ship.status} />
            <div className="mt-3 text-[10px] tracking-widest uppercase font-bold text-[#475569]">Est. delivery</div>
            <div className="font-mono text-lg font-bold text-[#0E2F76]">{formatDateShort(ship.eta)}</div>
            <div className="mt-3 flex gap-2 justify-end">
              <button onClick={()=>window.print()} className="text-xs font-semibold text-[#0E2F76] hover:text-[#d41f26] inline-flex items-center gap-1 no-print" data-testid="print-shipment"><Printer size={12}/> Print</button>
            </div>
          </div>
        </div>
        <div className="mt-6">
          <div className="h-1.5 w-full bg-[#F4FEFF] border border-[#A9C0E0]/40">
            <div className="h-full bg-[#0E2F76]" style={{ width: `${ship.progress_pct || 0}%` }} />
          </div>
        </div>
      </div>

      {ship.status === "AWAITING_APPROVAL" && (
        staff
          ? <ApprovalBar ship={ship} onChange={load} />
          : (
            <div className="vtx-card p-5 mb-6 flex items-start gap-3 border-l-4 border-[#B45309]" data-testid="awaiting-approval-banner">
              <Clock size={18} className="text-[#B45309] mt-0.5 shrink-0" />
              <div>
                <div className="font-bold text-[#0E2F76]">Awaiting approval</div>
                <div className="text-sm text-[#475569] mt-1">Your booking has been submitted and is being reviewed by our team. Tracking will begin once it&apos;s confirmed.</div>
              </div>
            </div>
          )
      )}

      {staff && ship.status !== "AWAITING_APPROVAL" && <OpsActions ship={ship} onChange={load} />}

      <Tabs defaultValue="timeline" className="mt-6">
        <TabsList className="bg-white border border-[#A9C0E0]/40 max-w-full overflow-x-auto" style={{ borderRadius: 2 }}>
          <TabsTrigger value="timeline" data-testid="tab-timeline">Timeline</TabsTrigger>
          <TabsTrigger value="routing" data-testid="tab-routing">Routing</TabsTrigger>
          <TabsTrigger value="cargo" data-testid="tab-cargo">Cargo</TabsTrigger>
          <TabsTrigger value="parties" data-testid="tab-parties">Shipper & consignee</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents">Documents</TabsTrigger>
          <TabsTrigger value="notes" data-testid="tab-notes">Notes</TabsTrigger>
        </TabsList>

        <TabsContent value="timeline" className="mt-4">
          <div className="grid lg:grid-cols-5 gap-4">
            <div className="lg:col-span-3 vtx-card p-6"><TrackingTimeline events={ship.events || []} currentStatus={ship.status} legs={ship.legs || []} /></div>
            <div className="lg:col-span-2 vtx-card p-4">
              <div className="vtx-eyebrow mb-2">Route</div>
              <TrackingMap route={route} loading={routeLoading} height={360} />
              {route?.ais && (
                <div className="mt-2 text-[10px] uppercase tracking-widest font-bold text-[#d41f26]">Live AIS · {route.ais.provider} · MMSI {route.ais.vessel_mmsi}</div>
              )}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="routing" className="mt-4">
          {staff
            ? <div className="space-y-4"><MarginCard ship={ship} onRate={load} /><RoutingPanel ship={ship} onChange={load} /></div>
            : <RoutingView legs={ship.legs || []} />}
        </TabsContent>

        <TabsContent value="cargo" className="mt-4">
          <div className="vtx-card overflow-x-auto">
            <table className="vtx-table w-full">
              <thead><tr><th>Description</th><th>Pieces</th><th>Weight/pc</th><th>Dims (cm)</th><th>HS code</th><th>Declared value</th></tr></thead>
              <tbody>
                {(ship.items || []).map((it, i) => (
                  <tr key={i}>
                    <td>{it.description}{it.dangerous_goods && <span className="ml-2 vtx-chip" style={{background:"#FEE2E2", color:"#B91C1C"}}>DG</span>}</td>
                    <td className="font-mono">{it.pieces}</td>
                    <td className="font-mono">{it.weight_kg} kg</td>
                    <td className="font-mono text-xs">{it.length_cm}×{it.width_cm}×{it.height_cm}</td>
                    <td className="font-mono text-xs">{it.hs_code || "—"}</td>
                    <td className="font-mono">{formatMoney(it.declared_value, it.currency)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="parties" className="mt-4">
          <div className="grid md:grid-cols-2 gap-4">
            <PartyCard title="Shipper" p={ship.shipper} />
            <PartyCard title="Consignee" p={ship.consignee} />
          </div>
        </TabsContent>

        <TabsContent value="documents" className="mt-4">
          <div className="vtx-card p-8 text-center">
            <FileText size={30} className="mx-auto text-[#A9C0E0]"/>
            <div className="mt-3 font-bold text-[#0E2F76]">No documents yet</div>
            <div className="mt-1 text-sm text-[#475569]">Bill of lading / air waybill, invoice, packing list, customs docs.</div>
            <div className="mt-4 text-xs text-[#475569]">Document uploads are coming soon.</div>
          </div>
        </TabsContent>

        <TabsContent value="notes" className="mt-4">
          <div className="vtx-card">
            <div className="p-5 border-b border-[#A9C0E0]/40 vtx-eyebrow">Communication thread</div>
            <div className="p-5 space-y-4 max-h-[400px] overflow-y-auto">
              {(ship.notes_thread || []).length === 0 && <div className="text-sm text-[#475569]">No notes yet.</div>}
              {(ship.notes_thread || []).map((n, i) => (
                <div key={i} className="border-l-2 border-[#0E2F76] pl-4">
                  <div className="text-xs font-semibold text-[#0E2F76]">{n.author_name} <span className="text-[#475569] font-mono ml-2">{formatDate(n.created_at)}</span></div>
                  <div className="text-sm mt-1 whitespace-pre-wrap">{n.body}</div>
                </div>
              ))}
            </div>
            <div className="p-5 border-t border-[#A9C0E0]/40">
              <textarea rows={3} className="vtx-input" placeholder="Type a note visible to ops and finance…" value={note} onChange={(e)=>setNote(e.target.value)} data-testid="note-input" />
              <div className="mt-3 flex justify-end"><button className="vtx-btn-primary" disabled={busy} onClick={addNote} data-testid="note-submit">Post note</button></div>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </PortalLayout>
  );
}

function PartyCard({ title, p }) {
  return (
    <div className="vtx-card p-5">
      <div className="vtx-eyebrow">{title}</div>
      <div className="mt-3 font-bold text-[#0E2F76]">{p.name}</div>
      {p.company && <div className="text-sm text-[#475569]">{p.company}</div>}
      <div className="text-sm mt-2">{p.address_line1}{p.address_line2 && `, ${p.address_line2}`}</div>
      <div className="text-sm">{p.city}, {p.country} {p.postal_code || ""}</div>
      {p.contact_email && <div className="text-xs font-mono text-[#475569] mt-2">{p.contact_email}</div>}
      {p.contact_phone && <div className="text-xs font-mono text-[#475569]">{p.contact_phone}</div>}
    </div>
  );
}

const LEG_MODES = [
  { v: "road", l: "Road" },
  { v: "air", l: "Air" },
  { v: "sea", l: "Sea" },
];
const LEG_STATUSES = [
  { v: "PENDING", l: "Planned" },
  { v: "IN_TRANSIT", l: "In transit" },
  { v: "COMPLETED", l: "Completed" },
  { v: "EXCEPTION", l: "Exception" },
];

// Read-only journey view for customers.
function RoutingView({ legs }) {
  if (!legs.length) return <div className="vtx-card p-6 text-sm text-[#475569]">No routing yet.</div>;
  return (
    <div className="vtx-card p-6 space-y-4" data-testid="routing-view">
      <div className="vtx-eyebrow flex items-center gap-2"><Route size={14} /> Journey</div>
      {[...legs].sort((a, b) => a.sequence - b.sequence).map((l) => (
        <div key={l.sequence} className="flex flex-wrap items-center gap-2 border-l-2 border-[#0E2F76] pl-3">
          <span className="text-[10px] font-black tracking-widest uppercase text-[#475569]">Leg {l.sequence}</span>
          <ModeIcon mode={l.mode} size={13} />
          <span className="text-sm font-semibold text-[#0E2F76]">{l.origin}</span>
          <span className="text-[#A9C0E0]">→</span>
          <span className="text-sm font-semibold text-[#0E2F76]">{l.destination}</span>
          {l.status && <StatusChip status={l.status} className="ml-1" />}
        </div>
      ))}
    </div>
  );
}

// Forwarder P&L — leg cost vs quoted price. Staff only.
function CostMetric({ label, value, color = "#0E2F76" }) {
  return (
    <div className="border-l-2 pl-3" style={{ borderColor: color }}>
      <div className="text-[9px] tracking-[0.22em] uppercase font-bold text-[#475569]">{label}</div>
      <div className="mt-1 font-mono text-lg font-bold" style={{ color }}>{value}</div>
    </div>
  );
}

function MarginCard({ ship, onRate }) {
  const [busy, setBusy] = useState(false);
  const fin = ship.finance || {};
  const legs = [...(ship.legs || [])].sort((a, b) => a.sequence - b.sequence);
  const margin = fin.margin;
  const marginColor = margin == null ? "#475569" : margin >= 0 ? "#166534" : "#B91C1C";

  const rateAll = async () => {
    setBusy(true);
    try {
      await api.post(`/v1/shipments/${ship.id}/rate`);
      await onRate();
      toast.success("Legs rated against lane rate cards");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="vtx-card p-5" data-testid="margin-card">
      <div className="flex items-center justify-between mb-4">
        <div className="vtx-eyebrow flex items-center gap-2"><DollarSign size={14} /> Cost & margin</div>
        <button className="vtx-btn-primary text-xs inline-flex items-center gap-1.5" disabled={busy} onClick={rateAll} data-testid="rate-all-legs">
          <Calculator size={13} /> Rate all legs
        </button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <CostMetric label="Leg cost" value={formatMoney(fin.cost_total, fin.currency)} />
        <CostMetric label="Quoted price" value={fin.quoted_amount != null ? formatMoney(fin.quoted_amount, fin.currency) : "—"} />
        <CostMetric label="Margin" value={margin != null ? formatMoney(margin, fin.currency) : "—"} color={marginColor} />
        <CostMetric label="Margin %" value={fin.margin_pct != null ? `${fin.margin_pct}%` : "—"} color={marginColor} />
      </div>
      {!fin.fully_rated && (
        <div className="mt-3 text-xs text-[#475569]" data-testid="rate-hint">
          {(fin.rated_legs || 0)} of {(fin.total_legs || 0)} legs rated · click “Rate all legs”.
        </div>
      )}
      <div className="mt-4 border-t border-[#A9C0E0]/40 pt-3 space-y-1.5">
        {legs.map((l) => (
          <div key={l.sequence} className="flex items-center justify-between text-sm">
            <span className="text-[#475569]">
              Leg {l.sequence} · {l.origin} → {l.destination}
              <span className="ml-2 text-[10px] uppercase tracking-wider font-bold text-[#A9C0E0]">{l.carrier_type === "partner" ? "partner" : "self"}</span>
            </span>
            <span className="font-mono font-semibold text-[#0E2F76]">{l.cost_amount != null ? formatMoney(l.cost_amount, l.cost_currency) : "—"}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// Editable routing for ops/admin — build the multi-leg plan, assign carriers.
function RoutingPanel({ ship, onChange }) {
  const seed = (ship.legs || []).map((l) => ({
    mode: l.mode || "road", origin: l.origin || "", destination: l.destination || "",
    carrier_type: l.carrier_type || "self", carrier_name: l.carrier_name || "Ventrix",
    status: l.status || "PENDING",
  }));
  const [legs, setLegs] = useState(seed.length ? seed : [
    { mode: ship.mode || "road", origin: ship.origin || "", destination: ship.destination || "", carrier_type: "self", carrier_name: "Ventrix", status: "PENDING" },
  ]);
  const [busy, setBusy] = useState(false);
  const [carriers, setCarriers] = useState([]);
  useEffect(() => { api.get("/v1/carriers").then(({ data }) => setCarriers(data.items || [])).catch(() => {}); }, []);

  const setLeg = (i, patch) => setLegs((prev) => prev.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  const addLeg = () => setLegs((prev) => {
    const last = prev[prev.length - 1];
    return [...prev, { mode: last?.mode || "road", origin: last?.destination || "", destination: "", carrier_type: "self", carrier_name: "Ventrix", status: "PENDING" }];
  });
  const removeLeg = (i) => setLegs((prev) => prev.filter((_, idx) => idx !== i));

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        legs: legs.map((l) => ({
          mode: l.mode,
          origin: l.origin,
          destination: l.destination,
          carrier_type: l.carrier_type,
          carrier_name: l.carrier_name || (l.carrier_type === "self" ? "Ventrix" : ""),
          status: l.status,
        })),
      };
      await api.put(`/v1/shipments/${ship.id}/legs`, payload);
      await onChange();
      toast.success("Routing updated");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="vtx-card p-5 bg-[#F4FEFF]/60" data-testid="routing-panel">
      <datalist id="vtx-carriers">
        {carriers.map((c) => <option key={c.id} value={c.name} />)}
      </datalist>
      <div className="flex items-center justify-between mb-4">
        <div className="vtx-eyebrow flex items-center gap-2"><Route size={14} /> Route plan</div>
        <button className="text-xs font-semibold text-[#0E2F76] hover:text-[#d41f26] inline-flex items-center gap-1" onClick={addLeg} data-testid="routing-add-leg"><Plus size={13} /> Add leg</button>
      </div>
      <div className="space-y-3">
        {legs.map((l, i) => (
          <div key={i} className="bg-white border border-[#A9C0E0]/50 p-3" style={{ borderRadius: 2 }} data-testid={`routing-leg-${i}`}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] font-black tracking-widest uppercase text-[#475569]">Leg {i + 1}</span>
              {legs.length > 1 && (
                <button className="text-[#d41f26] hover:opacity-70" onClick={() => removeLeg(i)} data-testid={`routing-remove-${i}`}><Trash2 size={14} /></button>
              )}
            </div>
            <div className="grid md:grid-cols-2 gap-2">
              <input className="vtx-input" placeholder="Origin" value={l.origin} onChange={(e) => setLeg(i, { origin: e.target.value })} data-testid={`routing-origin-${i}`} />
              <input className="vtx-input" placeholder="Destination" value={l.destination} onChange={(e) => setLeg(i, { destination: e.target.value })} data-testid={`routing-destination-${i}`} />
              <select className="vtx-input" value={l.mode} onChange={(e) => setLeg(i, { mode: e.target.value })} data-testid={`routing-mode-${i}`}>
                {LEG_MODES.map((m) => <option key={m.v} value={m.v}>{m.l}</option>)}
              </select>
              <select className="vtx-input" value={l.status} onChange={(e) => setLeg(i, { status: e.target.value })} data-testid={`routing-status-${i}`}>
                {LEG_STATUSES.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
              </select>
              <select className="vtx-input" value={l.carrier_type} onChange={(e) => setLeg(i, { carrier_type: e.target.value, carrier_name: e.target.value === "self" ? "Ventrix" : (l.carrier_name === "Ventrix" ? "" : l.carrier_name) })} data-testid={`routing-carrier-type-${i}`}>
                <option value="self">Self-operated (Ventrix)</option>
                <option value="partner">Partner carrier</option>
              </select>
              <input className="vtx-input" placeholder="Carrier name" list="vtx-carriers" value={l.carrier_name} disabled={l.carrier_type === "self"} onChange={(e) => setLeg(i, { carrier_name: e.target.value })} data-testid={`routing-carrier-name-${i}`} />
            </div>
          </div>
        ))}
      </div>
      <div className="mt-4 flex justify-end">
        <button className="vtx-btn-primary inline-flex items-center gap-2" disabled={busy} onClick={save} data-testid="routing-save"><Save size={14} /> Save routing</button>
      </div>
    </div>
  );
}

function ApprovalBar({ ship, onChange }) {
  const [busy, setBusy] = useState(false);
  const approve = async () => {
    setBusy(true);
    try {
      await api.post(`/v1/shipments/${ship.id}/approve`);
      await onChange();
      toast.success("Booking approved");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="vtx-card p-5 mb-6 flex flex-wrap items-center justify-between gap-4 border-l-4 border-[#B45309]" data-testid="approval-bar">
      <div className="flex items-start gap-3">
        <Clock size={18} className="text-[#B45309] mt-0.5 shrink-0" />
        <div>
          <div className="font-bold text-[#0E2F76]">Awaiting your approval</div>
          <div className="text-sm text-[#475569] mt-1">Review the booking details below, then confirm to start tracking. Milestones can&apos;t be pushed until it&apos;s approved.</div>
        </div>
      </div>
      <button className="vtx-btn-primary inline-flex items-center gap-2" disabled={busy} onClick={approve} data-testid="approve-shipment">
        <CheckCircle2 size={15} /> {busy ? "Approving…" : "Approve booking"}
      </button>
    </div>
  );
}

function OpsActions({ ship, onChange }) {
  const [code, setCode] = useState("IN_TRANSIT");
  const [descr, setDescr] = useState("");
  const [loc, setLoc] = useState("");
  const [eta, setEta] = useState("");   // optional ETA override (YYYY-MM-DD)
  const [excReason, setExcReason] = useState("DELAY");
  const [excDesc, setExcDesc] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmPush, setConfirmPush] = useState(false);   // confirm before updating
  const [confirmUndo, setConfirmUndo] = useState(false);

  const pushEvent = async () => {
    setBusy(true);
    try {
      await api.post(`/v1/shipments/${ship.id}/events`, {
        status_code: code, description: descr || `Status set to ${code}`,
        location: loc || null,
        eta: eta ? new Date(eta + "T00:00:00Z").toISOString() : null,
      });
      setDescr(""); setLoc(""); setEta(""); setConfirmPush(false);
      await onChange(); toast.success("Milestone pushed. A customer email is waiting in the review queue.");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const undoLast = async () => {
    setBusy(true);
    try {
      await api.post(`/v1/shipments/${ship.id}/events/undo`);
      setConfirmUndo(false); await onChange(); toast.success("Last milestone undone");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const flag = async () => {
    setBusy(true);
    try {
      await api.post(`/v1/shipments/${ship.id}/exception`, { reason_code: excReason, description: excDesc });
      setExcDesc(""); await onChange(); toast.success("Exception flagged");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="vtx-card p-5 mb-6 bg-[#F4FEFF]/60">
      <div className="flex items-center gap-2 mb-4">
        <Zap size={16} className="text-[#0E2F76]" />
        <div className="vtx-eyebrow">Ops actions</div>
      </div>
      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs font-bold text-[#0E2F76]">Push milestone</div>
            <button className="text-xs text-[#d41f26] font-bold hover:underline inline-flex items-center gap-1 disabled:opacity-40"
              disabled={busy} onClick={()=>setConfirmUndo(true)} data-testid="ops-undo-milestone"><Trash2 size={12}/> Undo last</button>
          </div>
          <div className="grid grid-cols-1 gap-3">
            <Select value={code} onValueChange={setCode}>
              <SelectTrigger data-testid="ops-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>{STATUS_CODES.map(s => <SelectItem key={s} value={s}>{s.replace(/_/g," ")}</SelectItem>)}</SelectContent>
            </Select>
            <input className="vtx-input" placeholder="Location (e.g., Rotterdam terminal 3)" value={loc} onChange={(e)=>setLoc(e.target.value)} data-testid="ops-location" />
            <input className="vtx-input" placeholder="Description" value={descr} onChange={(e)=>setDescr(e.target.value)} data-testid="ops-description" />
            <label className="block">
              <span className="text-[11px] text-[#475569] block mb-1">ETA override (optional — auto-set from the milestone if blank)</span>
              <input type="date" className="vtx-input" value={eta} onChange={(e)=>setEta(e.target.value)} data-testid="ops-eta" />
            </label>
            <button className="vtx-btn-primary" disabled={busy} onClick={()=>setConfirmPush(true)} data-testid="ops-push-event">Push milestone</button>
          </div>
        </div>
        <div>
          <div className="text-xs font-bold text-[#d41f26] mb-2 inline-flex items-center gap-1"><AlertOctagon size={12}/> Flag exception</div>
          <div className="grid grid-cols-1 gap-3">
            <Select value={excReason} onValueChange={setExcReason}>
              <SelectTrigger data-testid="ops-exc-select"><SelectValue /></SelectTrigger>
              <SelectContent>{EXCEPTION_CODES.map(x => <SelectItem key={x.v} value={x.v}>{x.l}</SelectItem>)}</SelectContent>
            </Select>
            <textarea rows={2} className="vtx-input" placeholder="Describe the issue" value={excDesc} onChange={(e)=>setExcDesc(e.target.value)} data-testid="ops-exc-desc" />
            <button className="vtx-btn-danger" disabled={busy || !excDesc} onClick={flag} data-testid="ops-flag-exception">Flag exception</button>
          </div>
        </div>
      </div>

      {/* Confirm before pushing a milestone — prevents accidental updates. */}
      <Dialog open={confirmPush} onOpenChange={setConfirmPush}>
        <DialogContent className="bg-white max-w-md" data-testid="confirm-push-dialog">
          <DialogHeader><DialogTitle>Confirm milestone</DialogTitle></DialogHeader>
          <div className="text-sm text-[#334155]">
            Set this shipment to <span className="font-bold text-[#0E2F76]">{code.replace(/_/g," ")}</span>
            {loc ? <> at <span className="font-semibold">{loc}</span></> : null}?
            <div className="mt-2 text-xs text-[#475569]">
              {eta ? <>ETA will be set to <span className="font-semibold">{eta}</span>.</> : "The ETA will be recalculated automatically from this milestone."}
              {" "}A customer email will be queued for your review — it won't send automatically.
            </div>
          </div>
          <DialogFooter>
            <button className="vtx-btn-ghost" onClick={()=>setConfirmPush(false)}>Cancel</button>
            <button className="vtx-btn-primary" disabled={busy} onClick={pushEvent} data-testid="confirm-push-yes">Confirm &amp; update</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Undo the most recently pushed milestone. */}
      <Dialog open={confirmUndo} onOpenChange={setConfirmUndo}>
        <DialogContent className="bg-white max-w-md" data-testid="confirm-undo-dialog">
          <DialogHeader><DialogTitle>Undo last milestone?</DialogTitle></DialogHeader>
          <div className="text-sm text-[#334155]">
            This removes the most recent milestone and reverts the shipment's status, progress and ETA to the prior state. The booking approval can&apos;t be undone.
          </div>
          <DialogFooter>
            <button className="vtx-btn-ghost" onClick={()=>setConfirmUndo(false)}>Cancel</button>
            <button className="vtx-btn-danger" disabled={busy} onClick={undoLast} data-testid="confirm-undo-yes">Undo milestone</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
